import Dexie, { type Table } from 'dexie'
import type {
  OfflineArea,
  OfflineBill,
  OfflineBooking,
  OfflineCatalogItem,
  OfflineIdMapping,
  OfflineLegacyRecord,
  OfflineOrder,
  OfflinePosCommand,
  OfflinePosContext,
  OfflinePricingSnapshot,
  OfflineSalesTable,
  OfflineSession,
  OfflineSyncIssue,
  OfflineVoucher,
} from './contracts'

interface LegacyProduct extends Record<string, unknown> {
  id: string
}

interface LegacySyncQueueItem extends Record<string, unknown> {
  id: number
}

export class BigSaleDB extends Dexie {
  offlineContexts!: Table<OfflinePosContext, string>
  areas!: Table<OfflineArea, [string, number]>
  salesTables!: Table<OfflineSalesTable, [string, number]>
  sessions!: Table<OfflineSession, [string, string]>
  orders!: Table<OfflineOrder, [string, string]>
  bills!: Table<OfflineBill, [string, string]>
  bookings!: Table<OfflineBooking, [string, string]>
  catalog!: Table<OfflineCatalogItem, [string, number, number]>
  pricingSnapshots!: Table<OfflinePricingSnapshot, [string, number]>
  outboxCommands!: Table<OfflinePosCommand, string>
  idMappings!: Table<OfflineIdMapping, [string, string, string]>
  syncIssues!: Table<OfflineSyncIssue, number>
  legacyRecords!: Table<OfflineLegacyRecord, number>
  vouchers!: Table<OfflineVoucher, [string, number]>

  constructor(name = 'bigsale-db') {
    super(name)

    this.version(1).stores({
      products: 'id, name, sku, updated_at',
      syncQueue: '++id, status, created_at',
    })

    this.version(2)
      .stores({
        products: null,
        syncQueue: null,
        offlineContexts: '&context_key, [tenant_code+branch_id+device_id], mode',
        areas: '&[context_key+id], context_key, branch_id, sort_order',
        salesTables: '&[context_key+id], context_key, area_id, status',
        sessions: '&[context_key+local_id], [context_key+server_id], context_key, table_id, status',
        orders: '&[context_key+session_local_id], context_key, session_local_id',
        bills:
          '&[context_key+local_id], &[context_key+offline_number], [context_key+server_id], context_key, session_local_id, sync_status',
        catalog:
          '&[context_key+table_id+product_id], [context_key+table_id], [context_key+product_id], context_key, product_id, category_id, sku, name',
        pricingSnapshots: '&[context_key+table_id], context_key, bootstrap_id',
        outboxCommands:
          '&command_id, [context_key+sequence], [context_key+status], [context_key+aggregate_local_id], previous_command_id, type',
        idMappings: '&[context_key+entity_type+local_id], [context_key+entity_type+server_id]',
        syncIssues: '++id, context_key, command_id, category, resolved_at',
        legacyRecords: '++id, source, legacy_key, migrated_at',
      })
      .upgrade(async (transaction) => {
        const [products, syncQueue] = await Promise.all([
          transaction.table('products').toArray() as Promise<LegacyProduct[]>,
          transaction.table('syncQueue').toArray() as Promise<LegacySyncQueueItem[]>,
        ])
        const migratedAt = new Date().toISOString()
        const records: OfflineLegacyRecord[] = [
          ...products.map((product) => ({
            source: 'products' as const,
            legacy_key: product.id,
            payload: structuredClone(product),
            migrated_at: migratedAt,
          })),
          ...syncQueue.map((queueItem) => ({
            source: 'syncQueue' as const,
            legacy_key: queueItem.id,
            payload: structuredClone(queueItem),
            migrated_at: migratedAt,
          })),
        ]

        if (records.length > 0) {
          await transaction.table('legacyRecords').bulkAdd(records)
        }
      })

    this.version(3).stores({
      offlineContexts: '&context_key, [tenant_code+branch_id+device_id], mode',
      areas: '&[context_key+id], context_key, branch_id, sort_order',
      salesTables: '&[context_key+id], context_key, area_id, status',
      sessions: '&[context_key+local_id], [context_key+server_id], context_key, table_id, status',
      orders: '&[context_key+session_local_id], context_key, session_local_id',
      bills:
        '&[context_key+local_id], &[context_key+offline_number], [context_key+server_id], context_key, session_local_id, sync_status',
      bookings:
        '&[context_key+local_id], [context_key+server_id], context_key, status, arrival_time',
      catalog:
        '&[context_key+table_id+product_id], [context_key+table_id], [context_key+product_id], context_key, product_id, category_id, sku, name',
      pricingSnapshots: '&[context_key+table_id], context_key, bootstrap_id',
      outboxCommands:
        '&command_id, [context_key+sequence], [context_key+status], [context_key+aggregate_local_id], previous_command_id, type',
      idMappings: '&[context_key+entity_type+local_id], [context_key+entity_type+server_id]',
      syncIssues: '++id, context_key, command_id, category, resolved_at',
      legacyRecords: '++id, source, legacy_key, migrated_at',
    })

    this.version(4).stores({
      offlineContexts: '&context_key, [tenant_code+branch_id+device_id], mode',
      areas: '&[context_key+id], context_key, branch_id, sort_order',
      salesTables: '&[context_key+id], context_key, area_id, status',
      sessions: '&[context_key+local_id], [context_key+server_id], context_key, table_id, status',
      orders: '&[context_key+session_local_id], context_key, session_local_id',
      bills:
        '&[context_key+local_id], &[context_key+offline_number], [context_key+server_id], context_key, session_local_id, sync_status',
      bookings:
        '&[context_key+local_id], [context_key+server_id], context_key, status, arrival_time',
      catalog:
        '&[context_key+table_id+product_id], [context_key+table_id], [context_key+product_id], context_key, product_id, category_id, sku, name',
      vouchers: '&[context_key+id], [context_key+code], [context_key+status]',
      pricingSnapshots: '&[context_key+table_id], context_key, bootstrap_id',
      outboxCommands:
        '&command_id, [context_key+sequence], [context_key+status], [context_key+aggregate_local_id], previous_command_id, type',
      idMappings: '&[context_key+entity_type+local_id], [context_key+entity_type+server_id]',
      syncIssues: '++id, context_key, command_id, category, resolved_at',
      legacyRecords: '++id, source, legacy_key, migrated_at',
    })
  }
}

export function createBigSaleDB(name?: string): BigSaleDB {
  return new BigSaleDB(name)
}

export const db = new BigSaleDB()
